import React, { useState, useEffect } from 'react';
import { FileText, ListTodo, BarChart3, Plus, X } from 'lucide-react';
import { supabase } from '../../services/supabaseService';
import { maintenanceService } from '../../services/maintenanceService';
import type { MaintenanceContract, ContractStatus } from '../../types/maintenance';
import ContractCard from './ContractCard';
import ContractDetail from './ContractDetail';
import TasksList from './TasksList';
import TaskForm from './TaskForm';
import MonthlyReportView from './MonthlyReportView';

type Tab = 'contracts' | 'tasks' | 'reports';

const MaintenanceModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('contracts');
  const [contracts, setContracts] = useState<MaintenanceContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [showNewContract, setShowNewContract] = useState(false);
  const [addTaskContractId, setAddTaskContractId] = useState<string | null>(null);
  const [selectedTasksContract, setSelectedTasksContract] = useState<string>('');

  const [clients, setClients] = useState<Array<{ id: string; full_name: string; email: string | null; entity_type: string }>>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [contractError, setContractError] = useState('');
  useEffect(() => {
    if (!showNewContract) return;
    let active = true;
    setLoadingClients(true);
    setContractError('');
    void (async () => {
      try {
        const { data, error } = await supabase.from('contacts')
          .select('id,full_name,email,entity_type').eq('is_active', true).is('deleted_at', null).order('full_name');
        if (error) throw error;
        if (active) setClients(data || []);
      } catch (error) {
        if (active) { setClients([]); setContractError((error as Error).message || 'No se pudieron cargar los clientes'); }
      } finally { if (active) setLoadingClients(false); }
    })();
    return () => { active = false; };
  }, [showNewContract]);

  // New contract form state
  const [form, setForm] = useState({
    contact_id: '',
    client_name: '',
    plan_name: 'Plan Mantenimiento',
    monthly_price: 0,
    monthly_hours: 10,
    support_hours: 6,
    improvement_hours: 4,
    extra_hour_price: 65,
    start_date: new Date().toISOString().split('T')[0],
    payment_method: 'SEPA',
    billing_day: 1,
    status: 'active' as ContractStatus,
    notes: '',
  });
  const [savingContract, setSavingContract] = useState(false);

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const data = await maintenanceService.getContracts();
      setContracts(data);
      if (data.length > 0 && !selectedTasksContract) {
        setSelectedTasksContract(data[0].id);
      }
    } catch (err) {
      console.error('Error loading contracts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContract = async (e: React.FormEvent) => {
    e.preventDefault();
    const client = clients.find(c => c.id === form.contact_id);
    if (!client || loadingClients) return;
    setContractError('');
    setSavingContract(true);
    try {
      await maintenanceService.createContract({ ...form, contact_id: client.id, client_name: client.full_name });
      setShowNewContract(false);
      setForm({
        contact_id: '', client_name: '', plan_name: 'Plan Mantenimiento', monthly_price: 0,
        monthly_hours: 10, support_hours: 6, improvement_hours: 4, extra_hour_price: 65,
        start_date: new Date().toISOString().split('T')[0], payment_method: 'SEPA',
        billing_day: 1, status: 'active', notes: '',
      });
      loadContracts();
    } catch (err) {
      console.error('Error creating contract:', err);
      setContractError((err as Error).message || 'No se pudo crear el contrato');
    } finally {
      setSavingContract(false);
    }
  };

  const handleTaskSave = async (data: any) => {
    await maintenanceService.createTask(data);
    setAddTaskContractId(null);
    loadContracts();
  };

  // If viewing contract detail, render that
  if (selectedContractId) {
    return (
      <div className="p-6">
        <ContractDetail
          contractId={selectedContractId}
          onBack={() => { setSelectedContractId(null); loadContracts(); }}
        />
      </div>
    );
  }

  const tabs = [
    { id: 'contracts' as Tab, label: 'Contratos activos', icon: FileText },
    { id: 'tasks' as Tab, label: 'Todas las tareas', icon: ListTodo },
    { id: 'reports' as Tab, label: 'Informes', icon: BarChart3 },
  ];

  const renderContracts = () => (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowNewContract(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nuevo contrato
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando contratos...</div>
      ) : contracts.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No hay contratos de mantenimiento</p>
          <button
            onClick={() => setShowNewContract(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Crear primer contrato
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contracts.map((c) => (
            <ContractCard
              key={c.id}
              contract={c}
              onViewDetail={setSelectedContractId}
              onAddTask={(id) => { const c = contracts.find(x => x.id === id); if (c?.project_id) alert('Registra las horas desde Mantenimiento dentro del proyecto.'); else setAddTaskContractId(id); }}
            />
          ))}
        </div>
      )}
    </div>
  );

  const renderTasks = () => (
    <div>
      {contracts.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Contrato</label>
          <select
            value={selectedTasksContract}
            onChange={(e) => setSelectedTasksContract(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>{c.client_name} — {c.plan_name}</option>
            ))}
          </select>
        </div>
      )}
      {selectedTasksContract ? (
        contracts.find(c => c.id === selectedTasksContract)?.project_id ? <p>Las intervenciones de este contrato se registran desde la pestaña Mantenimiento del proyecto.</p> : <TasksList contractId={selectedTasksContract} />
      ) : (
        <p className="text-gray-500 text-center py-8">Selecciona un contrato</p>
      )}
    </div>
  );

  const renderReports = () => (
    <div>
      {contracts.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Contrato</label>
          <select
            value={selectedTasksContract}
            onChange={(e) => setSelectedTasksContract(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>{c.client_name} — {c.plan_name}</option>
            ))}
          </select>
        </div>
      )}
      {selectedTasksContract && (
        <ReportsList
          contractId={selectedTasksContract}
          contract={contracts.find((c) => c.id === selectedTasksContract)!}
          onViewReport={(month) => {
            setSelectedContractId(null);
            // Navigate to contract detail with report open
            const c = contracts.find((c) => c.id === selectedTasksContract);
            if (c) setSelectedContractId(c.id);
          }}
        />
      )}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'contracts': return renderContracts();
      case 'tasks': return renderTasks();
      case 'reports': return renderReports();
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Mantenimiento SLA</h1>
        <p className="text-gray-600">Gestion de contratos de mantenimiento, tareas y horas</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        <div className="p-6">
          {renderContent()}
        </div>
      </div>

      {/* New Contract Modal */}
      {showNewContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Nuevo contrato de mantenimiento</h3>
              <button onClick={() => setShowNewContract(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateContract} className="p-4 space-y-4">
              {contractError && <p role="alert" className="text-red-700 bg-red-50 p-3 rounded-lg">{contractError}</p>}
              <div>
                <label htmlFor="maintenance-client" className="block text-sm font-medium text-gray-700 mb-1">Cliente del CRM *</label>
                <select
                  id="maintenance-client"
                  value={form.contact_id}
                  onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                  disabled={loadingClients || savingContract}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">{loadingClients ? 'Cargando clientes…' : 'Selecciona un cliente existente'}</option>
                  {clients.map(client => <option key={client.id} value={client.id}>
                    {client.full_name} · {client.entity_type === 'company' ? 'Empresa' : 'Persona'}{client.email ? ` · ${client.email}` : ''}
                  </option>)}
                </select>
                {!loadingClients && clients.length === 0 && !contractError && <p className="text-sm text-gray-600 mt-2">No hay clientes activos. Añade primero el cliente al CRM.</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del plan</label>
                <input
                  type="text"
                  value={form.plan_name}
                  onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio mensual (€)</label>
                  <input
                    type="number"
                    value={form.monthly_price}
                    onChange={(e) => setForm({ ...form, monthly_price: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Horas mensuales totales</label>
                  <input
                    type="number"
                    value={form.monthly_hours}
                    onChange={(e) => setForm({ ...form, monthly_hours: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Horas soporte</label>
                  <input
                    type="number"
                    value={form.support_hours}
                    onChange={(e) => setForm({ ...form, support_hours: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Horas mejoras</label>
                  <input
                    type="number"
                    value={form.improvement_hours}
                    onChange={(e) => setForm({ ...form, improvement_hours: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio hora extra (€)</label>
                  <input
                    type="number"
                    value={form.extra_hour_price}
                    onChange={(e) => setForm({ ...form, extra_hour_price: parseFloat(e.target.value) || 0 })}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de pago</label>
                  <select
                    value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="SEPA">SEPA</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Tarjeta">Tarjeta</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dia facturacion</label>
                  <input
                    type="number"
                    value={form.billing_day}
                    onChange={(e) => setForm({ ...form, billing_day: parseInt(e.target.value) || 1 })}
                    min="1"
                    max="28"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de inicio</label>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowNewContract(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingContract || loadingClients || !clients.some(c => c.id === form.contact_id)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingContract ? 'Creando...' : 'Crear contrato'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {addTaskContractId && (
        <TaskForm
          contractId={addTaskContractId}
          onSave={handleTaskSave}
          onClose={() => setAddTaskContractId(null)}
        />
      )}
    </div>
  );
};

// ─── Reports list sub-component ──────────────────────────────

interface ReportsListProps {
  contractId: string;
  contract: MaintenanceContract;
  onViewReport: (month: string) => void;
}

const ReportsList: React.FC<ReportsListProps> = ({ contractId, contract }) => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewingMonth, setViewingMonth] = useState<string | null>(null);

  useEffect(() => {
    loadReports();
  }, [contractId]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await maintenanceService.getReportsByContract(contractId);
      setReports(data);
    } catch (err) {
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    const month = new Date().toISOString().slice(0, 7);
    await maintenanceService.getOrCreateMonthlyReport(contractId, month);
    loadReports();
    setViewingMonth(month);
  };

  if (viewingMonth) {
    return (
      <MonthlyReportView
        contract={contract}
        reportMonth={viewingMonth}
        onBack={() => { setViewingMonth(null); loadReports(); }}
      />
    );
  }

  if (loading) return <div className="text-center py-8 text-gray-500">Cargando informes...</div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={handleGenerate}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Generar informe mes actual
        </button>
      </div>
      {reports.length === 0 ? (
        <p className="text-center py-8 text-gray-500">No hay informes generados</p>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
              onClick={() => setViewingMonth(r.report_month.slice(0, 7))}
            >
              <div>
                <p className="font-medium text-gray-900">
                  {new Date(r.report_month).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                </p>
                <p className="text-sm text-gray-500">
                  {Number(r.total_hours_used).toFixed(1)}h usadas de {r.included_hours}h
                  {Number(r.extra_hours) > 0 && ` | +${Number(r.extra_hours).toFixed(1)}h extra (${Number(r.extra_hours_cost).toFixed(0)}€)`}
                </p>
              </div>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                r.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {r.status === 'sent' ? 'Enviado' : 'Borrador'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MaintenanceModule;
